import { __extends } from "tslib";
import { wcfConfig } from "../WcfConfig";
import { CustomError } from "../error";
import { isNotNullOrEmpty } from "../string";
import { AjaxRequest } from "./internal/AjaxRequest";
/**
 * Will be thrown if the ajax request was aborted.
 */
var AjaxAbortedError = /** @class */ (function (_super) {
    __extends(AjaxAbortedError, _super);
    function AjaxAbortedError(pUrl) {
        var _this = _super.call(this, "request aborted: " + pUrl) || this;
        Object.setPrototypeOf(_this, AjaxAbortedError.prototype);
        _this.name = "AjaxAbortedError";
        return _this;
    }
    return AjaxAbortedError;
}(CustomError));
export { AjaxAbortedError };
/**
 * Will be thrown for ajax request timeouts.
 */
var AjaxTimeoutError = /** @class */ (function (_super) {
    __extends(AjaxTimeoutError, _super);
    function AjaxTimeoutError(pUrl) {
        var _this = _super.call(this, "request timeout: " + pUrl) || this;
        Object.setPrototypeOf(_this, AjaxTimeoutError.prototype);
        _this.name = "AjaxTimeoutError";
        return _this;
    }
    return AjaxTimeoutError;
}(CustomError));
export { AjaxTimeoutError };
/**
 * Will be thrown for ajax connection errors.
 */
var AjaxConnectionError = /** @class */ (function (_super) {
    __extends(AjaxConnectionError, _super);
    function AjaxConnectionError(pUrl) {
        var _this = _super.call(this, "request connection interrupted: " + pUrl) || this;
        Object.setPrototypeOf(_this, AjaxConnectionError.prototype);
        _this.name = "AjaxConnectionError";
        return _this;
    }
    return AjaxConnectionError;
}(CustomError));
export { AjaxConnectionError };
/**
 * Wrapper function for XMLHttpRequests with optional custom error handling.
 * pErrorMessageCallback can be used to return a custom error message.
 */
export function ajax(pMethod, pUrl, pData, pOptions, pCustomErrorCallback, pBeforeCallback) {
    var _a, _b;
    var tOptions = pOptions == null ? {} : pOptions;
    var tMaxRetries = (_a = tOptions.retryAttempts) !== null && _a !== void 0 ? _a : wcfConfig.ajaxDefaultOptions.retryAttempts;
    var tIgnoreGlobalErrorHandler = (_b = tOptions.ignoreGlobalErrorHandler) !== null && _b !== void 0 ? _b : wcfConfig.ajaxDefaultOptions.ignoreGlobalErrorHandler;
    var tCurrentRequest = null;
    var getRequestInfo = function () {
        return ({
            url: pUrl,
            method: pMethod,
            data: pData,
            options: pOptions
        });
    };
    var rejectRequest = function (pRejectData, pResponseError, pReject) {
        if (wcfConfig.ajaxRejectedEvent != null) {
            wcfConfig.ajaxRejectedEvent(getRequestInfo(), pRejectData.xhr, pResponseError);
        }
        pReject(pResponseError);
    };
    var executeRequest = function (pResolve, pReject, pTotalRetryCount, pRetryCount) {
        tCurrentRequest = new AjaxRequest(pMethod, pUrl, pData, tOptions, pBeforeCallback);
        tCurrentRequest.execute().then(function (pResolveData) {
            tCurrentRequest = null;
            if (wcfConfig.ajaxResolvedEvent != null) {
                wcfConfig.ajaxResolvedEvent(getRequestInfo(), pResolveData.xhr);
            }
            pResolve(pResolveData.response);
        }).catch(function (pRejectData) {
            tCurrentRequest = null;
            //check if request was aborted, in this case directly foward the error
            //do not trigger ajaxRejectedEvent for aborted requests
            if (pRejectData.error instanceof AjaxAbortedError) {
                pReject(pRejectData.error);
                return;
            }
            //in case of network errors automatically retry the request
            if (pRejectData.error instanceof AjaxConnectionError && pRetryCount < tMaxRetries) {
                setTimeout(function () {
                    executeRequest(pResolve, pReject, pTotalRetryCount + 1, pRetryCount + 1);
                }, wcfConfig.ajaxGetRetryDelay(pRetryCount + 1));
                return;
            }
            var tResponseError = getResponseError(pRejectData, pCustomErrorCallback);
            //delegate error information to error handler
            if (wcfConfig.ajaxErrorHandler != null && !tIgnoreGlobalErrorHandler) {
                var tErrorInfo = {
                    status: pRejectData.xhr.status,
                    textStatus: pRejectData.xhr.statusText,
                    errorThrown: pRejectData.error,
                    responseError: tResponseError,
                    requestInfo: getRequestInfo(),
                    retryCount: pTotalRetryCount,
                    retry: function () { return executeRequest(pResolve, pReject, pTotalRetryCount + 1, 0); },
                    reject: function (pCustomError) { return rejectRequest(pRejectData, pCustomError !== null && pCustomError !== void 0 ? pCustomError : tResponseError, pReject); }
                };
                wcfConfig.ajaxErrorHandler(tErrorInfo);
            }
            else {
                rejectRequest(pRejectData, tResponseError, pReject);
            }
        });
    };
    var tPromise = new Promise(function (pResolve, pReject) {
        executeRequest(pResolve, pReject, 0, 0);
    });
    tPromise.abort = function () {
        if (tCurrentRequest != null) {
            tCurrentRequest.abort();
        }
    };
    return (tPromise);
}
function getResponseError(pRejectData, pCustomErrorCallback) {
    var tErrorMessage = pRejectData.error instanceof Error ? pRejectData.error.message : pRejectData.error;
    var tResponseError = null;
    if (pCustomErrorCallback != null)
        tResponseError = pCustomErrorCallback(pRejectData.error, pRejectData.xhr, pRejectData.response);
    if (tResponseError == null) {
        tResponseError = "[" + pRejectData.xhr.status + "] " + pRejectData.xhr.statusText;
        if (isNotNullOrEmpty(tErrorMessage))
            tResponseError += " - " + tErrorMessage;
    }
    if (tResponseError instanceof Error)
        return (tResponseError);
    else
        return Error(tResponseError);
}
